import { useEffect, useRef, useState } from 'react';
import { Check, X, Sparkles, Star, Zap, Eye, Search, MapPin, Circle, Square, Triangle, Hexagon, ArrowRight, Undo2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Icon mapping for different part types
 * Maps common keywords to appropriate icons
 */
const getIconForPart = (partId, label) => {
  const idLower = partId.toLowerCase();
  const labelLower = label.toLowerCase();
  
  // Cell-related icons
  if (idLower.includes('nucleus') || labelLower.includes('nucleus')) return Circle;
  if (idLower.includes('membrane') || labelLower.includes('membrane')) return Square;
  if (idLower.includes('cytoplasm') || labelLower.includes('cytoplasm')) return Hexagon;
  if (idLower.includes('mitochondria') || labelLower.includes('mitochondria')) return Triangle;
  if (idLower.includes('ribosome') || labelLower.includes('ribosome')) return Circle;
  
  // Generic discovery icons
  if (idLower.includes('control') || labelLower.includes('control')) return Zap;
  if (idLower.includes('energy') || labelLower.includes('energy')) return Zap;
  if (idLower.includes('protein') || labelLower.includes('protein')) return Star;
  if (idLower.includes('dna') || labelLower.includes('dna')) return Sparkles;
  
  // Default to search/eye icon for discovery
  return Search;
};

/**
 * InteractiveDiagram component - Match the following (left: icons, right: descriptions)
 * Data structure: { parts: [{id, label, description, position: {x, y}, icon_type?}], background_image_description }
 */
export default function InteractiveDiagram({ simulationData, onComplete }) {
  // 360 navigation state

  // Pan and zoom state
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const offsetStart = useRef({ x: 0, y: 0 });

  // Diagram image (optional). Backend commonly only provides a description.
  const diagramImage =
    simulationData.diagram_image ||
    simulationData.diagramImage ||
    simulationData.image ||
    simulationData.image_url ||
    simulationData.background_image ||
    null;
  const backgroundImageDescription =
    simulationData.background_image_description ||
    simulationData.backgroundImageDescription ||
    simulationData.background_description ||
    '';

  // Pan handlers
  const handlePanStart = (e) => {
    setIsPanning(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    panStart.current = { x: clientX, y: clientY };
    offsetStart.current = { ...offset };
  };
  const handlePanMove = (e) => {
    if (!isPanning) return;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    setOffset({
      x: offsetStart.current.x + (clientX - panStart.current.x),
      y: offsetStart.current.y + (clientY - panStart.current.y),
    });
  };
  const handlePanEnd = () => setIsPanning(false);

  // Add event listeners for pan
  useEffect(() => {
    if (!isPanning) return;
    const move = (e) => handlePanMove(e);
    const up = () => handlePanEnd();
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, [isPanning]);

  // Zoom handlers
  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.2, 5));
  const handleZoomOut = () => setZoom((z) => Math.max(z / 1.2, 0.2));
  const handleResetView = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) return; // Let browser handle pinch-to-zoom
    e.preventDefault();
    const delta = e.deltaY;
    if (delta < 0) handleZoomIn();
    else handleZoomOut();
  };

  const [parts, setParts] = useState(() => {
    // Shuffle parts for random order
    const shuffled = [...(simulationData.parts || [])];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });
  
  const [descriptions, setDescriptions] = useState(() => {
    // Shuffle descriptions separately
    const shuffled = [...(simulationData.parts || [])];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });
  
  const [selectedLeft, setSelectedLeft] = useState(null); // Selected part (left side)
  const [selectedRight, setSelectedRight] = useState(null); // Selected description (right side)
  const [matchedPairs, setMatchedPairs] = useState(new Map()); // Map of partId -> description index
  const [matchHistory, setMatchHistory] = useState([]); // Track match history for undo: [{partId, descriptionIndex, timestamp}]
  const [matchAnimations, setMatchAnimations] = useState({}); // Animation state for matched pairs
  const [wrongMatch, setWrongMatch] = useState(null); // Track wrong matches for shake animation

  const handleLeftClick = (part) => {
    if (matchedPairs.has(part.id)) return; // Already matched
    
    if (selectedLeft?.id === part.id) {
      // Deselect if clicking the same item
      setSelectedLeft(null);
    } else {
      setSelectedLeft(part);
      // If right is already selected, try to match
      if (selectedRight !== null) {
        checkMatch(part, selectedRight);
      }
    }
  };

  const handleRightClick = (description, index) => {
    // Check if this description is already matched
    const isMatched = Array.from(matchedPairs.values()).includes(index);
    if (isMatched) return;
    
    if (selectedRight === index) {
      // Deselect if clicking the same item
      setSelectedRight(null);
    } else {
      setSelectedRight(index);
      // If left is already selected, try to match
      if (selectedLeft !== null) {
        checkMatch(selectedLeft, index);
      }
    }
  };

  const checkMatch = (part, descriptionIndex) => {
    const description = descriptions[descriptionIndex];
    
    // Check if this is the correct match
    if (part.id === description.id) {
      // Correct match!
      setMatchedPairs(prev => new Map([...prev, [part.id, descriptionIndex]]));
      
      // Add to match history for undo
      setMatchHistory(prev => [...prev, {
        partId: part.id,
        descriptionIndex: descriptionIndex,
        timestamp: Date.now()
      }]);
      
      // Trigger match animation
      setMatchAnimations(prev => ({ 
        ...prev, 
        [`left-${part.id}`]: true,
        [`right-${descriptionIndex}`]: true
      }));
      
      setTimeout(() => {
        setMatchAnimations(prev => {
          const newAnim = { ...prev };
          delete newAnim[`left-${part.id}`];
          delete newAnim[`right-${descriptionIndex}`];
          return newAnim;
        });
      }, 1500);
      
      // Clear selections
      setSelectedLeft(null);
      setSelectedRight(null);
      
      // Check completion
      if (matchedPairs.size + 1 >= parts.length) {
        setTimeout(() => {
          if (onComplete) onComplete();
        }, 2000);
      }
    } else {
      // Wrong match - shake animation
      setWrongMatch({ left: part.id, right: descriptionIndex });
      setTimeout(() => {
        setWrongMatch(null);
        setSelectedLeft(null);
        setSelectedRight(null);
      }, 600);
    }
  };

  const handleUndo = () => {
    if (matchHistory.length === 0) return;
    
    // Get the last match
    const lastMatch = matchHistory[matchHistory.length - 1];
    
    // Remove from matched pairs
    setMatchedPairs(prev => {
      const newMap = new Map(prev);
      newMap.delete(lastMatch.partId);
      return newMap;
    });
    
    // Remove from history
    setMatchHistory(prev => prev.slice(0, -1));
    
    // Clear selections
    setSelectedLeft(null);
    setSelectedRight(null);
  };

  const handleResetAll = () => {
    if (matchedPairs.size === 0) return;
    
    // Clear all matches
    setMatchedPairs(new Map());
    setMatchHistory([]);
    setSelectedLeft(null);
    setSelectedRight(null);
  };

  const allMatched = matchedPairs.size >= parts.length;

  return (
    <div className="space-y-4">
      {/* 360 Navigation / Interactive Diagram Viewer with Pan & Zoom */}
      <div className="relative flex flex-col items-center justify-center mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Button variant="outline" size="icon" onClick={handleZoomOut} title="Zoom Out" style={{ color: '#0f766e', borderColor: '#0f766e', background: 'white' }}>
            <span style={{ fontSize: 22, fontWeight: 'bold', lineHeight: 1 }}>-</span>
          </Button>
          <Button variant="outline" size="icon" onClick={handleResetView} title="Reset View" style={{ color: '#0f766e', borderColor: '#0f766e', background: 'white' }}>
            <RotateCcw className="w-5 h-5" />
          </Button>
          <Button variant="outline" size="icon" onClick={handleZoomIn} title="Zoom In" style={{ color: '#0f766e', borderColor: '#0f766e', background: 'white' }}>
            <span style={{ fontSize: 22, fontWeight: 'bold', lineHeight: 1 }}>+</span>
          </Button>
        </div>
        <div
          className="relative w-64 h-64 bg-gray-100 dark:bg-gray-800 border rounded-lg flex items-center justify-center overflow-hidden select-none cursor-grab"
          style={{ touchAction: 'none' }}
          onMouseDown={handlePanStart}
          onTouchStart={handlePanStart}
          onWheel={handleWheel}
        >
          {diagramImage ? (
            <img
              src={diagramImage}
              alt="Interactive Diagram"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transition: isPanning ? 'none' : 'transform 0.2s',
                cursor: isPanning ? 'grabbing' : 'grab',
                userSelect: 'none',
              }}
              draggable={false}
            />
          ) : (
            <div
              className="px-4 text-center"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transition: isPanning ? 'none' : 'transform 0.2s',
              }}
            >
              <div className="text-6xl opacity-30">🔬</div>
              {backgroundImageDescription ? (
                <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 max-w-[14rem]">
                  {backgroundImageDescription}
                </div>
              ) : (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 max-w-[14rem]">
                  Diagram preview not available
                </div>
              )}
            </div>
          )}
          {/* Overlay: Pan/zoom hint */}
          <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-gray-500 bg-white/70 dark:bg-gray-900/70 px-2 py-0.5 rounded">Drag to pan, scroll to zoom</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">360° Navigation (Pan & Zoom)</div>
      </div>

      <div className="relative bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-2 border-teal-300 dark:border-teal-700 rounded-lg p-6 min-h-[500px]">
        {/* Background description (visual representation) */}
        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
          <div className="text-6xl">🔬</div>
        </div>

        {/* Instructions and Undo Controls */}
        <div className="flex items-center justify-between mb-4 z-10 relative">
          <p className="text-sm font-semibold text-teal-700 dark:text-teal-300">
            Match each part with its description
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleUndo}
              variant="outline"
              size="sm"
              className="text-xs"
              disabled={matchHistory.length === 0}
              title={matchHistory.length > 0 ? "Undo last match" : "No matches to undo"}
            >
              <Undo2 className="w-3 h-3 mr-1" />
              Undo
            </Button>
            {matchedPairs.size > 1 && (
              <Button
                onClick={handleResetAll}
                variant="outline"
                size="sm"
                className="text-xs text-red-600 hover:text-red-700 hover:border-red-400"
                title="Reset all matches"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset All
              </Button>
            )}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-2 gap-6 relative z-10">
          {/* Left Column - Parts/Icons */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">
              Parts
            </h3>
            {parts.map((part) => {
              const isMatched = matchedPairs.has(part.id);
              const isSelected = selectedLeft?.id === part.id;
              const isAnimating = matchAnimations[`left-${part.id}`];
              const isWrong = wrongMatch?.left === part.id;
              const PartIcon = getIconForPart(part.id, part.label);
              
              return (
                <button
                  key={part.id}
                  onClick={() => handleLeftClick(part)}
                  disabled={isMatched}
                  className={`w-full p-4 rounded-lg border-2 transition-all duration-300 transform ${
                    isMatched
                      ? 'bg-green-500/10 border-green-500 cursor-default'
                      : isSelected
                      ? 'bg-cyan-500/10 border-cyan-400 scale-105 shadow-lg ring-2 ring-cyan-300/60 ring-offset-2 ring-offset-background'
                      : isWrong
                      ? 'bg-red-500/10 border-red-500 animate-shake'
                      : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] hover:border-cyan-300 hover:shadow-md'
                  } ${isAnimating ? 'ring-4 ring-green-400 animate-pulse' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                      isMatched
                        ? 'bg-green-500 text-white'
                        : 'bg-teal-500 text-white'
                    }`}>
                      {isMatched ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <PartIcon className="w-6 h-6" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {part.label}
                      </div>
                      {isMatched && (
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          ✓ Matched
                        </div>
                      )}
                    </div>
                    {isSelected && !isMatched && (
                      <ArrowRight className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    )}
                    {isAnimating && (
                      <Sparkles className="w-5 h-5 text-yellow-500 animate-spin" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Right Column - Descriptions */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">
              Descriptions
            </h3>
            {descriptions.map((description, index) => {
              const isMatched = Array.from(matchedPairs.values()).includes(index);
              const isSelected = selectedRight === index;
              const isAnimating = matchAnimations[`right-${index}`];
              const isWrong = wrongMatch?.right === index;
              // Candidate = right card is clickable when a left item is selected
              const isCandidate = !!selectedLeft && !isMatched && !isWrong;
              // Correct target for the currently selected left item
              const isCorrectTarget = !!selectedLeft && description.id === selectedLeft.id && !isMatched;
              
              return (
                <button
                  key={`desc-${index}`}
                  onClick={() => handleRightClick(description, index)}
                  disabled={isMatched}
                  className={`w-full p-4 rounded-lg border-2 transition-all duration-300 transform ${
                    isMatched
                      ? 'bg-green-200 dark:bg-green-700 border-green-600 cursor-default text-green-900 dark:text-green-50 font-semibold shadow-md'
                      : isSelected
                      ? 'bg-cyan-100 dark:bg-cyan-900/30 border-cyan-500 scale-105 shadow-lg ring-2 ring-cyan-300/60 ring-offset-2 ring-offset-background text-gray-900 dark:text-gray-100 font-semibold'
                      : isWrong
                      ? 'bg-red-100 dark:bg-red-900/20 border-red-500 animate-shake text-red-900 dark:text-red-50 font-semibold'
                      : isCorrectTarget
                      ? 'bg-cyan-300 dark:bg-cyan-800/50 border-cyan-600 shadow-lg text-cyan-900 dark:text-cyan-50 font-semibold'
                      : isCandidate
                      ? 'bg-white dark:bg-[hsl(var(--card-bg))] border-gray-300 dark:border-[hsl(var(--card-border))] hover:border-cyan-300 hover:shadow-md text-gray-900 dark:text-gray-100'
                      : 'bg-white dark:bg-[hsl(var(--card-bg))] border-gray-300 dark:border-[hsl(var(--card-border))] hover:border-cyan-300 hover:shadow-md text-gray-900 dark:text-gray-100'
                  } ${isAnimating ? 'ring-4 ring-green-400 animate-pulse' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    {isSelected && !isMatched && (
                      <ArrowRight className="w-5 h-5 text-teal-600 dark:text-teal-400 transform rotate-180" />
                    )}
                    <div className="flex-1 text-left">
                      <p className="text-sm leading-snug text-gray-900 dark:text-gray-100">
                        {description.description}
                      </p>
                      {isMatched && (
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          ✓ Matched
                        </div>
                      )}
                    </div>
                    {isMatched && (
                      <Check className="w-6 h-6 text-green-500 flex-shrink-0" />
                    )}
                    {isAnimating && (
                      <Sparkles className="w-5 h-5 text-yellow-500 animate-spin" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Progress indicator */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Star className="w-4 h-4 text-yellow-500 animate-pulse" />
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-teal-600 dark:text-teal-400">
              {matchedPairs.size}
            </span>
            {' / '}
            <span className="font-semibold">{parts.length}</span>
            {' matches found!'}
          </div>
          <Star className="w-4 h-4 text-yellow-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-teal-500 via-cyan-500 to-teal-500 h-3 rounded-full transition-all duration-500 relative overflow-hidden"
            style={{ width: `${(matchedPairs.size / parts.length) * 100}%` }}
          >
            {/* Shimmer effect on progress bar */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
          </div>
        </div>
        {allMatched && (
          <div className="mt-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-3 shadow-lg">
            <div className="flex items-center justify-center gap-2">
              <Star className="w-5 h-5 animate-spin" style={{ animationDuration: '2s' }} />
              <p className="font-semibold">
                🎉 Perfect! All matches found!
              </p>
              <Star className="w-5 h-5 animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
